import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Columns3, X } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { DEPARTMENTS, statusColor, statusLabel } from "@/constants/workflow";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DEFAULT_COLUMNS = [
  { key: "panel_name", label: "Panel Name", width: 210, mono: true },
  { key: "panel_uid", label: "Panel UID", width: 240, mono: true, optional: true, hidden: true },
  { key: "panel_type", label: "Type", width: 110, mono: true },
  { key: "level", label: "Level", width: 70, mono: true },
  { key: "elevation", label: "Elev", width: 60, mono: true },
  { key: "status_design", label: "Design", width: 130, status: "design" },
  { key: "status_bim", label: "BIM", width: 130, status: "bim" },
  { key: "status_production", label: "Production", width: 130, status: "production" },
  { key: "status_qaqc", label: "QA/QC", width: 120, status: "qaqc" },
  { key: "qaqc_passed_on", label: "QA/QC Date", width: 115, mono: true },
  { key: "status_logistics", label: "Logistics", width: 110, status: "logistics" },
  { key: "pallet_code", label: "Pallet", width: 100, mono: true },
  { key: "shipping_method", label: "Shipping", width: 120 },
  { key: "shipment_code", label: "Shipment", width: 110, mono: true },
  { key: "shipped_on", label: "Shipped Date", width: 115, mono: true },
  { key: "estimated_arrival", label: "ETA", width: 100, mono: true },
  { key: "delivered_on", label: "Arrival Date", width: 115, mono: true },
  { key: "installed_on", label: "Installed On", width: 115, mono: true },
  { key: "status_site", label: "Site", width: 110, status: "site" },
  { key: "workflow_state", label: "Workflow", width: 130 },
  { key: "open_issues", label: "Issues", width: 70 },
  { key: "updated_at", label: "Last Updated", width: 150 },
  { key: "updated_by", label: "Updated By", width: 140 },
  { key: "glass_properties", label: "Glass Props", width: 150, optional: true, hidden: true },
  { key: "glass_manufacturer", label: "Glass Mfr", width: 140, optional: true, hidden: true },
  { key: "project_code", label: "Project", width: 80, optional: true, hidden: true, mono: true },
  { key: "type_code", label: "Type Code", width: 90, optional: true, hidden: true, mono: true },
  { key: "panel_code", label: "Panel Code", width: 100, optional: true, hidden: true, mono: true },
  { key: "unique_code", label: "Unique", width: 80, optional: true, hidden: true, mono: true },
  { key: "block_definition", label: "Block Def", width: 130, optional: true, hidden: true, mono: true },
];

const COLUMN_PREF_KEY = "okn-panel-columns-v1";
const COLUMN_KEYS = new Set(DEFAULT_COLUMNS.map((c) => c.key));

function cellValue(panel, column) {
  if (column.status) return statusLabel(panel.statuses?.[column.status]);
  if (column.key === "workflow_state")
    return panel.workflow_blocked ? `Blocked · ${panel.blocked_by}` : "OK";
  if (column.key === "updated_at")
    return panel.updated_at ? new Date(panel.updated_at).toLocaleDateString() : "—";
  if (column.key === "estimated_arrival")
    return panel.estimated_arrival
      ? new Date(panel.estimated_arrival).toLocaleDateString()
      : "—";
  if (column.key === "shipped_on")
    return panel.shipped_on ? new Date(panel.shipped_on).toLocaleDateString() : "—";
  if (column.key === "delivered_on")
    return panel.delivered_on ? new Date(panel.delivered_on).toLocaleDateString() : "—";
  if (column.key === "installed_on")
    return panel.installed_on ? new Date(panel.installed_on).toLocaleDateString() : "—";
  if (column.key === "qaqc_passed_on")
    return panel.qaqc_passed_on ? new Date(panel.qaqc_passed_on).toLocaleDateString() : "—";
  const value = panel[column.key];
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

export function PanelTable({
  panels,
  selection,
  onSelect,
  onClose,
  height = 300,
}) {
  const [hidden, setHidden] = useState(() => {
    // Remember the user's column layout between visits.
    try {
      const saved = JSON.parse(localStorage.getItem(COLUMN_PREF_KEY) || "null");
      if (Array.isArray(saved)) return new Set(saved.filter((key) => COLUMN_KEYS.has(key)));
    } catch (error) {
      /* ignore malformed preference */
      console.debug("[panel-table] column preference unreadable", error);
    }
    return new Set(DEFAULT_COLUMNS.filter((c) => c.hidden).map((c) => c.key));
  });
  const parentRef = useRef(null);
  const { isMobile } = useIsMobile();

  const columns = useMemo(
    () => DEFAULT_COLUMNS.filter((column) => !hidden.has(column.key)),
    [hidden]
  );

  const virtualizer = useVirtualizer({
    count: panels.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isMobile ? 92 : 30),
    overscan: 18,
  });

  const toggleColumn = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setHidden(next);
    try {
      localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify([...next]));
    } catch (error) {
      /* storage unavailable — layout simply is not remembered */
      console.debug("[panel-table] column preference not saved", error);
    }
  };

  return (
    <div
      data-testid="panel-table"
      className="flex shrink-0 flex-col border-t border-slate-200 bg-white"
      style={{ height }}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-200 px-3 py-2">
        {onClose ? (
          <button
            data-testid="table-toggle-button"
            onClick={onClose}
            className="mono okn-tool rounded-full border border-slate-200 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-600 hover:bg-slate-100"
          >
            Table ▾
          </button>
        ) : null}
        <span
          data-testid="table-row-count"
          className="mono rounded bg-slate-100 px-2 py-0.5 text-[10px] text-cyan-700"
        >
          {panels.length} panel{panels.length === 1 ? "" : "s"}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="columns-menu-trigger"
                className="okn-tool hidden items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 hover:bg-slate-100 md:flex"
              >
                <Columns3 size={12} /> Columns
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-80 overflow-y-auto border-slate-200 bg-white text-slate-800">
              {DEFAULT_COLUMNS.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={!hidden.has(column.key)}
                  onCheckedChange={() => toggleColumn(column.key)}
                  data-testid={`column-toggle-${column.key}`}
                  className="text-xs"
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {onClose ? (
            <button
              data-testid="close-table-button"
              onClick={onClose}
              className="okn-tool grid h-7 w-7 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="okn-scroll min-h-0 flex-1 overflow-auto" ref={parentRef}>
        {isMobile ? (
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualizer.getVirtualItems().map((item) => {
              const panel = panels[item.index];
              const selected = selection.includes(panel.panel_uid);
              return (
                <div
                  key={panel.panel_uid}
                  data-testid={`table-row-${panel.panel_name}`}
                  onClick={(event) => onSelect(panel.panel_uid, event.shiftKey)}
                  className={`absolute left-0 w-full border-b border-slate-200 px-3 py-2 ${
                    selected ? "bg-cyan-500/12" : ""
                  }`}
                  style={{ height: item.size, transform: `translateY(${item.start}px)` }}
                >
                  <div className="flex items-center gap-2">
                    <p className="mono truncate text-[12px] font-medium text-slate-900">
                      {panel.panel_name}
                    </p>
                    {panel.workflow_blocked ? (
                      <span className="mono ml-auto rounded bg-red-500/12 px-1.5 text-[9px] uppercase text-red-600">
                        blocked
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {DEPARTMENTS.map((dept) => (
                      <span
                        key={dept.key}
                        style={{ color: statusColor(dept.key, panel.statuses?.[dept.key]) }}
                        className="mono rounded bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
                      >
                        {dept.label.slice(0, 4)} · {statusLabel(panel.statuses?.[dept.key])}
                      </span>
                    ))}
                  </div>
                  <p className="mono mt-1 truncate text-[9.5px] uppercase tracking-wider text-slate-500">
                    {panel.pallet_code || "no pallet"} ·{" "}
                    {panel.shipment_code || "no shipment"} ·{" "}
                    {panel.shipped_on
                      ? new Date(panel.shipped_on).toLocaleDateString()
                      : "not shipped"}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
        <div style={{ minWidth: columns.reduce((sum, c) => sum + c.width, 0) }}>
          <div className="sticky top-0 z-10 flex border-b border-slate-200 bg-white">
            {columns.map((column) => (
              <div
                key={column.key}
                style={{ width: column.width }}
                className="mono shrink-0 px-2.5 py-2 text-[9.5px] uppercase tracking-wider text-slate-500"
              >
                {column.label}
              </div>
            ))}
          </div>
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualizer.getVirtualItems().map((item) => {
              const panel = panels[item.index];
              const selected = selection.includes(panel.panel_uid);
              return (
                <div
                  key={panel.panel_uid}
                  data-testid={`table-row-${panel.panel_name}`}
                  onClick={(event) => onSelect(panel.panel_uid, event.shiftKey)}
                  className={`absolute left-0 flex w-full cursor-pointer items-center border-b border-slate-200 ${
                    selected ? "bg-cyan-500/12" : "hover:bg-slate-50"
                  }`}
                  style={{ height: item.size, transform: `translateY(${item.start}px)` }}
                >
                  {columns.map((column) => {
                    const value = cellValue(panel, column);
                    const color = column.status
                      ? statusColor(column.status, panel.statuses?.[column.status])
                      : undefined;
                    return (
                      <div
                        key={column.key}
                        style={{ width: column.width, color }}
                        className={`shrink-0 truncate px-2.5 text-[11px] ${
                          column.mono ? "mono" : ""
                        } ${
                          column.key === "workflow_state" && panel.workflow_blocked
                            ? "text-red-600"
                            : color
                            ? ""
                            : "text-slate-700"
                        }`}
                      >
                        {value}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
